import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2, CheckCircle, AlertTriangle, AlertCircle, Clock,
  ChevronDown, ChevronRight, Loader2, Zap, Play, X, CheckSquare, RefreshCw, ExternalLink
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import CoverageCalendar, { getDefaultYears, getMonthsForYears } from './CoverageCalendar';
import AggregatedCoverageCalendar from './AggregatedCoverageCalendar';
import useCoverageAnimations from '../../hooks/useCoverageAnimations';
import './AgencyCoverageMatrix.css';

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const ms = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function freshnessCls(dateStr) {
  if (!dateStr) return 'stale';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days <= 7) return 'fresh';
  if (days <= 30) return 'aging';
  return 'stale';
}

function AgencyCoverageMatrix({ departmentCode, departmentId, refreshKey }) {
  const queryClient = useQueryClient();
  const [expandedDept, setExpandedDept] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [scraping, setScraping] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Per-department year selections: { deptId: [2021, 2022, ...] }
  const [deptYears, setDeptYears] = useState({});

  // Manual refresh of materialized view (recalculates all coverage data from scraped POs)
  const handleManualRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await apiClient.post('/api/gap-analysis/refresh');
      queryClient.invalidateQueries({ queryKey: ['gap-analysis', 'timeline'] });
      queryClient.invalidateQueries({ queryKey: ['freshness'] });
    } catch (e) {
      console.error('[AgencyCoverageMatrix] Manual refresh failed:', e);
    } finally {
      setRefreshing(false);
    }
  }, [queryClient]);

  // Refresh when parent signals (e.g., scrape completed via ScraperDashboard polling)
  React.useEffect(() => {
    if (refreshKey > 0) {
      queryClient.invalidateQueries({ queryKey: ['gap-analysis', 'timeline'] });
      queryClient.invalidateQueries({ queryKey: ['freshness'] });
    }
  }, [refreshKey, queryClient]);

  // Scope the timeline fetch to the selected department when one is active.
  // Without this, the backend recomputes detail/acq/overlay aggregates across
  // every department on every poll, which is the source of the long FTB load.
  const timelineDeptId = departmentId ?? null;
  const { data: timelineData, isLoading, error } = useQuery({
    queryKey: ['gap-analysis', 'timeline', timelineDeptId],
    queryFn: () => {
      const params = new URLSearchParams({ startDate: '2017-01-01' });
      if (timelineDeptId != null) params.set('departmentIds', String(timelineDeptId));
      return apiClient.get(`/api/gap-analysis/timeline?${params.toString()}`);
    },
    // Scrape-completion triggers an explicit invalidate; the background poll
    // only needs to catch out-of-band changes, so 60s is plenty.
    refetchInterval: 60000,
  });

  // Animation tracking — detects cells that just got filled, 100% completions
  const { justFilled, completedDepts, getSessionStats } = useCoverageAnimations(timelineData);

  const { data: freshnessData } = useQuery({
    queryKey: ['freshness'],
    queryFn: () => apiClient.get('/api/freshness'),
    refetchInterval: 60000,
  });

  // Poll active + recently completed jobs for cell overlays
  const { data: jobsData } = useQuery({
    queryKey: ['scraper', 'jobs', 'matrix-active'],
    queryFn: async () => {
      const [pending, claimed, running, recentCompleted] = await Promise.all([
        apiClient.get('/api/scraper/jobs?status=pending&limit=200'),
        apiClient.get('/api/scraper/jobs?status=claimed&limit=10'),
        apiClient.get('/api/scraper/jobs?status=running&limit=10'),
        apiClient.get('/api/scraper/jobs?status=completed&limit=50'),
      ]);
      return {
        jobs: [
          ...(running.jobs || []),
          ...(claimed.jobs || []),
          ...(pending.jobs || []),
          ...(recentCompleted.jobs || []),
        ]
      };
    },
    refetchInterval: 3000,
  });

  // Track previous active count — when it drops, jobs just finished, so refresh timeline
  const prevActiveRef = useRef(0);
  React.useEffect(() => {
    const active = (jobsData?.jobs || []).filter(j => ['pending', 'claimed', 'running'].includes(j.status)).length;
    if (prevActiveRef.current > 0 && active < prevActiveRef.current) {
      // Jobs just completed — refresh backend materialized view then invalidate cache
      (async () => {
        try {
          await apiClient.post('/api/gap-analysis/refresh');
        } catch (e) {
          console.error('[AgencyCoverageMatrix] Failed to refresh gap analysis view:', e);
        }
        queryClient.invalidateQueries({ queryKey: ['gap-analysis', 'timeline'] });
      })();
    }
    prevActiveRef.current = active;
  }, [jobsData, queryClient]);

  // Build job status lookup: "YYYY-MM:AcqType" → status
  // Show active states + recently completed (last 5 min) so cells don't flicker
  const getJobStatusMap = useCallback((deptCodeArg) => {
    // Use argument first, fall back to departmentCode prop for consistent filtering
    const deptCode = deptCodeArg || departmentCode;
    const allJobs = jobsData?.jobs || [];

    // Debug: log all jobs we received
    if (allJobs.length > 0) {
      console.log(`[getJobStatusMap] Received ${allJobs.length} total jobs, filtering for dept: ${deptCode}`);
    }

    const map = {};
    const fiveMinAgo = Date.now() - 5 * 60 * 1000;
    let matched = 0, filtered = 0;

    for (const j of allJobs) {
      const p = typeof j.params === 'string' ? JSON.parse(j.params) : (j.params || {});
      // Always filter by department if we have a code - prevents showing other depts' jobs
      if (deptCode && p.departmentCode !== deptCode) {
        filtered++;
        continue;
      }
      if (!p.dateFrom || !p.acquisitionType) continue;

      // Show active jobs always, completed only if recent (prevents flicker)
      const isActive = ['pending', 'claimed', 'running'].includes(j.status);
      const isRecentComplete = (j.status === 'completed' || j.status === 'partial')
        && j.completed_at && new Date(j.completed_at).getTime() > fiveMinAgo;
      if (!isActive && !isRecentComplete) continue;

      matched++;
      const mk = p.dateFrom.substring(0, 7);
      const key = `${mk}:${p.acquisitionType}`;
      const priority = { running: 4, claimed: 3, pending: 2, completed: 1, partial: 1 };
      if (!map[key] || (priority[j.status] || 0) > (priority[map[key]] || 0)) {
        map[key] = j.status;
      }
    }

    if (allJobs.length > 0) {
      console.log(`[getJobStatusMap] Result: ${matched} matched, ${filtered} filtered out, map keys:`, Object.keys(map));
    }

    return map;
  }, [jobsData, departmentCode]);

  const rescrape = useMutation({
    mutationFn: (code) => apiClient.post('/api/coverage/rescrape', { department_code: code }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gap-analysis'] }),
  });

  // Key by department_id so merged stubs sharing a name don't collide.
  // Falls back to department name only when the backend row has no id
  // (NULL department_id rows still occur in procurement_raw_data).
  const freshnessMap = useMemo(() => {
    const byId = {};
    const byName = {};
    for (const d of (freshnessData?.departments || [])) {
      if (d.department_id != null) byId[d.department_id] = d;
      else if (d.department) byName[d.department] = d;
    }
    return { byId, byName };
  }, [freshnessData]);

  // Helper: compute coverage stats for a type using given months
  const computeTypeCoverage = useCallback((monthMap, type, targetMonths) => {
    let monthsWithData = 0, totalPos = 0, monthsScrapedEmpty = 0;
    for (const mk of targetMonths) {
      const typeData = monthMap[mk]?.acq_types?.[type];
      const pos = typeData?.pos || 0;
      // Count actual data separately from scraped-but-empty
      if (pos > 0) {
        monthsWithData++;
        totalPos += pos;
      } else if (typeData?.scraped_empty) {
        monthsScrapedEmpty++;
      }
    }
    // Gaps = months without data AND not yet scraped (actionable)
    const gaps = targetMonths.length - monthsWithData - monthsScrapedEmpty;
    const pct = targetMonths.length > 0 ? Math.round((monthsWithData / targetMonths.length) * 100) : null;
    return { pct, months: monthsWithData, totalMonths: targetMonths.length, totalPos, gaps, scrapedEmpty: monthsScrapedEmpty };
  }, []);

  const deptRows = useMemo(() => {
    const timeline = Array.isArray(timelineData) ? timelineData : [];
    if (timeline.length === 0) return [];

    return timeline.map(dept => {
      const monthMap = {};
      for (const m of (dept.months || [])) {
        const key = m.month?.substring(0, 7);
        if (key) monthMap[key] = m;
      }
      const freshEntry =
        (dept.department_id != null && freshnessMap.byId[dept.department_id]) ||
        freshnessMap.byName[dept.department_name] ||
        {};
      return {
        department_id: dept.department_id,
        name: dept.department_name,
        acronym: dept.acronym || dept.department_name,
        code: dept.bu_code,
        freshDate: freshEntry.last_updated || null,
        monthMap,
      };
    });
  }, [timelineData, freshnessMap]);

  // Get active years for a department (falls back to default)
  const getActiveYears = useCallback((deptId) => deptYears[deptId] || getDefaultYears(), [deptYears]);

  const toggleYear = useCallback((deptId, year) => {
    setDeptYears(prev => {
      const current = prev[deptId] || getDefaultYears();
      const next = current.includes(year) ? current.filter(y => y !== year) : [...current, year].sort();
      if (next.length === 0) return prev; // must have at least one year
      return { ...prev, [deptId]: next };
    });
  }, []);

  const visibleRows = useMemo(() => {
    if (!departmentCode && !departmentId) return deptRows;
    return deptRows.filter(d => {
      // Match by bu_code OR department_id for flexibility
      if (departmentId && d.department_id === departmentId) return true;
      if (departmentCode && d.code === departmentCode) return true;
      // Also try matching departmentCode against department_id as string
      if (departmentCode && String(d.department_id) === String(departmentCode)) return true;
      return false;
    });
  }, [deptRows, departmentCode, departmentId]);

  React.useEffect(() => {
    if ((departmentCode || departmentId) && visibleRows.length === 1) {
      setExpandedDept(visibleRows[0].department_id);
    }
  }, [departmentCode, departmentId, visibleRows]);

  const summary = useMemo(() => ({
    withData: visibleRows.filter(d => Object.keys(d.monthMap).length > 0).length,
      completePct: (() => {
        // % of filled months that are confirmed ≥95% complete
        const allFilled = visibleRows.flatMap(d => Object.values(d.monthMap)).filter(m => m.has_data);
        const withCompleteness = allFilled.filter(m => m.completeness_pct !== null && m.completeness_pct !== undefined);
        const complete = withCompleteness.filter(m => m.completeness_pct >= 95);
        if (withCompleteness.length === 0) return null;
        return Math.round((complete.length / withCompleteness.length) * 100);
      })(),
    total: visibleRows.length,
    staleDepts: visibleRows.filter(d => freshnessCls(d.freshDate) === 'stale').length,
  }), [visibleRows]);

  // Selection handlers
  const toggleCell = useCallback((key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const selectAllEmpty = useCallback((monthMap, targetMonths) => {
    setSelected(prev => {
      const next = new Set(prev);
      for (const mk of targetMonths) {
        const md = monthMap[mk];
        const goods = md?.acq_types?.['IT Goods'];
        const services = md?.acq_types?.['IT Services'];
        // Skip cells that have data OR were already scraped and confirmed empty
        if (!(goods?.pos > 0) && !goods?.scraped_empty) next.add(`${mk}:IT Goods`);
        if (!(services?.pos > 0) && !services?.scraped_empty) next.add(`${mk}:IT Services`);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  // Queue scrape jobs for all selected cells
  const scrapeSelected = useCallback(async (deptCodeArg) => {
    // Use argument first, fall back to departmentCode prop
    const deptCode = deptCodeArg || departmentCode;
    if (!deptCode) {
      console.error('[AgencyCoverageMatrix] Cannot queue scrape: no department code available');
      alert('Unable to queue scrape: department code is missing. Please select a department or contact support.');
      return;
    }
    if (selected.size === 0) return;
    setScraping(true);
    try {
      const jobs = [];
      for (const key of selected) {
        const [monthKey, acqType] = key.split(':');
        const year = parseInt(monthKey.substring(0, 4));
        const month = parseInt(monthKey.substring(5, 7));
        const dateFrom = `${monthKey}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const dateTo = `${monthKey}-${String(lastDay).padStart(2, '0')}`;
        jobs.push(
          apiClient.post('/api/scraper/scrape/scprs', {
            departmentCode: deptCode,
            acquisitionType: acqType,
            dateFrom,
            dateTo,
          })
        );
      }
      console.log(`[AgencyCoverageMatrix] Queuing ${jobs.length} scrape jobs for dept ${deptCode}`,
        Array.from(selected).map(key => {
          const [monthKey, acqType] = key.split(':');
          return { monthKey, acqType, dateFrom: `${monthKey}-01` };
        })
      );
      const results = await Promise.allSettled(jobs);
      const succeeded = results.filter(r => r.status === 'fulfilled');
      const failed = results.filter(r => r.status === 'rejected');

      // Log successful responses to see what the API returned
      if (succeeded.length > 0) {
        console.log(`[AgencyCoverageMatrix] API responses:`, succeeded.map(r => r.value));
      }
      if (failed.length > 0) {
        console.error(`[AgencyCoverageMatrix] ${failed.length} of ${jobs.length} jobs failed to queue:`, failed.map(f => f.reason?.message || f.reason));
      }
      console.log(`[AgencyCoverageMatrix] Queued ${succeeded.length}/${jobs.length} jobs successfully`);
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['scraper', 'jobs'] });
    } catch (err) {
      console.error('Failed to queue scrape jobs:', err);
      alert(`Failed to queue scrape jobs: ${err.message}`);
    } finally {
      setScraping(false);
    }
  }, [selected, queryClient, departmentCode]);

  if (isLoading) {
    return <div className="sd-acm-loading"><Loader2 size={20} className="sd-spin" /> Loading coverage data...</div>;
  }
  if (error) {
    return <div className="sd-acm-error"><AlertCircle size={16} /> {error.message}</div>;
  }

  return (
    <div className="sd-acm">
      {/* Summary bar */}
      <div className="sd-acm-summary">
        <div className="sd-acm-stat">
          <span className="sd-acm-stat-value">{summary.withData}</span>
          <span className="sd-acm-stat-label">of {summary.total} depts have data</span>
        </div>
        <div className="sd-acm-stat">
          <span className="sd-acm-stat-value">{getDefaultYears().length}yr</span>
          <span className="sd-acm-stat-label">default range</span>
        </div>
        {summary.completePct !== null && (
          <div className="sd-acm-stat">
            <span className="sd-acm-stat-value" style={{ color: summary.completePct >= 80 ? 'var(--color-success)' : 'var(--color-warning)' }}>
              {summary.completePct}%
            </span>
            <span className="sd-acm-stat-label">scrapes ≥95% complete</span>
          </div>
        )}
        <div className={`sd-acm-stat ${summary.staleDepts > 0 ? 'sd-acm-stat-warn' : ''}`}>
          <span className="sd-acm-stat-value">{summary.staleDepts}</span>
          <span className="sd-acm-stat-label">stale (&gt;30d)</span>
        </div>
        <button
          className="sd-acm-refresh-btn"
          onClick={handleManualRefresh}
          disabled={refreshing}
          title="Recalculate coverage from all scraped data"
        >
          <RefreshCw size={14} className={refreshing ? 'sd-spin' : ''} />
          {refreshing ? 'Refreshing...' : 'Rebuild Coverage'}
        </button>
        <Link to="/data" className="sd-acm-link-btn" title="View full timeline with drilldown in Data Analysis">
          <ExternalLink size={14} />
          Full Timeline
        </Link>
      </div>

      {/* Aggregated calendar view for All Departments mode */}
      {!departmentCode && !departmentId && deptRows.length > 0 && (
        <AggregatedCoverageCalendar deptRows={deptRows} />
      )}

      {visibleRows.length === 0 ? (
        <div className="sd-acm-empty">
          <AlertTriangle size={20} />
          <p>{departmentCode ? 'No scraped data for this department yet' : 'No scraped data yet — run a scrape above'}</p>
        </div>
      ) : (
        <div className="sd-acm-table-wrap">
          <table className="sd-acm-table">
            <thead>
              <tr>
                <th>Department</th>
                <th>IT Goods</th>
                <th>IT Services</th>
                <th>Freshness</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(dept => {
                const isExpanded = expandedDept === dept.department_id;
                const years = getActiveYears(dept.department_id);
                const months = getMonthsForYears(years);
                const itGoods = computeTypeCoverage(dept.monthMap, 'IT Goods', months);
                const itServices = computeTypeCoverage(dept.monthMap, 'IT Services', months);
                const isRowComplete = completedDepts.has(`${dept.department_id}:IT Goods`) || completedDepts.has(`${dept.department_id}:IT Services`);
                return (
                  <React.Fragment key={dept.department_id}>
                    <tr
                      className={`sd-acm-row ${isExpanded ? 'sd-acm-row-expanded' : ''} ${isRowComplete ? 'sd-acm-row-complete' : ''}`}
                      onClick={() => setExpandedDept(isExpanded ? null : dept.department_id)}
                    >
                      <td data-label="Department" className="sd-acm-dept">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <Building2 size={14} />
                        <span className="sd-acm-acronym">{dept.acronym}</span>
                        <span className="sd-acm-name">{dept.name}</span>
                      </td>
                      <td data-label="IT Goods"><CoverageCell data={itGoods} /></td>
                      <td data-label="IT Services"><CoverageCell data={itServices} /></td>
                      <td data-label="Freshness"><FreshnessCell date={dept.freshDate} /></td>
                      <td data-label="Actions" className="sd-acm-actions">
                        {dept.code && (
                          <button
                            className="sd-acm-btn"
                            onClick={(e) => { e.stopPropagation(); rescrape.mutate(dept.code); }}
                            disabled={rescrape.isPending}
                            title="Queue scrapes for coverage gaps"
                          >
                            <Zap size={12} /> Scrape Gaps
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="sd-acm-detail-row">
                        <td colSpan={5}>
                          {/* Selection toolbar */}
                          {selected.size > 0 && (
                            <div className="sd-acm-sel-bar">
                              <span className="sd-acm-sel-count">
                                <CheckSquare size={14} />
                                {selected.size} cell{selected.size !== 1 ? 's' : ''} selected
                              </span>
                              <div className="sd-acm-sel-actions">
                                <button
                                  className="sd-acm-sel-btn sd-acm-sel-scrape"
                                  onClick={() => scrapeSelected(dept.code)}
                                  disabled={scraping || (!dept.code && !departmentCode)}
                                >
                                  {scraping
                                    ? <><Loader2 size={12} className="sd-spin" /> Queuing...</>
                                    : <><Play size={12} /> Scrape {selected.size} Selected</>
                                  }
                                </button>
                                <button className="sd-acm-sel-btn sd-acm-sel-clear" onClick={clearSelection}>
                                  <X size={12} /> Clear
                                </button>
                              </div>
                            </div>
                          )}
                          <CoverageCalendar
                            monthMap={dept.monthMap}
                            selected={selected}
                            jobStatuses={getJobStatusMap(dept.code)}
                            justFilled={justFilled}
                            onToggle={toggleCell}
                            onSelectAllEmpty={() => selectAllEmpty(dept.monthMap, months)}
                            activeYears={years}
                            onToggleYear={(y) => toggleYear(dept.department_id, y)}
                          />
                          {getSessionStats().gapsFilled > 0 && (
                            <div className="sd-acm-session-bar">
                              <span>{getSessionStats().gapsFilled} gap{getSessionStats().gapsFilled !== 1 ? 's' : ''} filled</span>
                              <span>{getSessionStats().posScraped} POs scraped this session</span>
                            </div>)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CoverageCell({ data }) {
  if (data.months === 0) return <span className="sd-acm-cov sd-acm-cov-none">No data</span>;
  const cls = data.pct >= 100 ? 'complete' : data.pct >= 50 ? 'partial' : 'low';
  const Icon = cls === 'complete' ? CheckCircle : cls === 'partial' ? AlertTriangle : AlertCircle;
  return (
    <span className={`sd-acm-cov sd-acm-cov-${cls}`}>
      <Icon size={12} /> {data.months}/{data.totalMonths} mo
      <span className="sd-acm-pos">({data.totalPos} POs)</span>
    </span>
  );
}

function FreshnessCell({ date }) {
  return (
    <span className={`sd-acm-fresh sd-acm-fresh-${freshnessCls(date)}`}>
      <Clock size={12} /> {timeAgo(date) || 'Never'}
    </span>
  );
}

/* MonthHeatmap (CoverageCalendar) and CellHalf extracted to CoverageCalendar.js */

export default AgencyCoverageMatrix;
